import { collection, doc, onSnapshot, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { sanitizeVotePlaces } from '../utils/voteTally';

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
export const VOTE_ID_PATTERN = /^[A-Za-z0-9]{20}$/;
const VOTE_HOURS = 24;

const randomId = length => Array.from(crypto.getRandomValues(new Uint8Array(length)), byte => ALPHABET[byte % ALPHABET.length]).join('');

class GroupVoteService {
  // Only public restaurant facts go into the shared document — never the explorer's own location.
  serializePlace(place) {
    const location = place.geometry?.location;
    const number = value => typeof value === 'function' ? value() : value;
    return {
      placeId: place.place_id,
      name: place.name || place.details?.name || '',
      vicinity: place.vicinity || place.formatted_address || '',
      cuisineLabel: place.cuisineLabel || '餐飲',
      walkMinutes: Number.isFinite(place.walkMinutes) ? place.walkMinutes : null,
      rating: Number.isFinite(place.rating) ? place.rating : null,
      reviewCount: Number.isFinite(place.user_ratings_total) ? place.user_ratings_total : 0,
      priceLevel: Number.isInteger(place.price_level) ? place.price_level : null,
      lat: location ? number(location.lat) : null,
      lng: location ? number(location.lng) : null
    };
  }

  async createVote(places, title) {
    const shared = places.filter(place => place?.place_id).slice(0, 5).map(place => this.serializePlace(place));
    if (shared.length < 2) throw new Error('At least two places are needed');
    const id = randomId(20);
    await setDoc(doc(db, 'votes', id), {
      title: String(title || '').slice(0, 60),
      places: shared,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + VOTE_HOURS * 3600000)
    });
    return id;
  }

  subscribe(voteId, { onVote, onBallots, onError }) {
    const stopVote = onSnapshot(doc(db, 'votes', voteId), snapshot => {
      if (!snapshot.exists()) return onVote(null);
      const data = snapshot.data();
      const expiresAt = typeof data.expiresAt?.toMillis === 'function' ? data.expiresAt.toMillis() : 0;
      onVote({ title: typeof data.title === 'string' ? data.title.slice(0, 60) : '', places: sanitizeVotePlaces(data.places), expired: expiresAt <= Date.now() });
    }, onError);
    const stopBallots = onSnapshot(collection(db, 'votes', voteId, 'ballots'), snapshot => {
      onBallots(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
    }, onError);
    return () => { stopVote(); stopBallots(); };
  }

  castBallot(voteId, voterId, name, picks) {
    return setDoc(doc(db, 'votes', voteId, 'ballots', voterId), {
      name: String(name || '').trim().slice(0, 20),
      picks,
      updatedAt: serverTimestamp()
    });
  }

  getVoterId() {
    try {
      const stored = localStorage.getItem('chi_na_jian_voter_id');
      if (/^[A-Za-z0-9]{24}$/.test(stored || '')) return stored;
      const id = randomId(24);
      localStorage.setItem('chi_na_jian_voter_id', id);
      return id;
    } catch { return randomId(24); }
  }

  shareUrl(voteId) {
    return `${window.location.origin}${window.location.pathname}?vote=${voteId}`;
  }
}

export default new GroupVoteService();
